/**
 * Session Manager Utility
 * Handles session persistence and restoration for chats
 */

import { STORAGE_KEYS, SESSION_PREFIXES } from './config';

export const SessionManager = {
    /**
     * Saves current chat session info to localStorage
     * @param {Object} params - Session parameters
     * @param {string|number} [params.chatId] - The chat ID
     * @param {string} [params.sessionId] - The session ID 
     * @param {string} [params.chatType] - The chat type ('standard' or 'advanced')
     */
    saveCurrentSession: (params) => {
        const { chatId, sessionId, chatType } = params;
        
        if (chatId) {
            localStorage.setItem(STORAGE_KEYS.CURRENT_CHAT_ID, chatId.toString());
        }
        
        if (sessionId) {
            localStorage.setItem(STORAGE_KEYS.CURRENT_SESSION, sessionId);
        }
        
        if (chatType) {
            localStorage.setItem(STORAGE_KEYS.CHAT_TYPE, chatType);
        }
    },
    
    /**
     * Clears the current session info from localStorage
     */
    clearCurrentSession: () => {
        localStorage.removeItem(STORAGE_KEYS.CURRENT_CHAT_ID);
        localStorage.removeItem(STORAGE_KEYS.CURRENT_SESSION);
        localStorage.removeItem(STORAGE_KEYS.CHAT_TYPE);
    },
    
    /**
     * Gets the current session info from localStorage
     * @returns {Object} Session info with chatId, sessionId, and chatType
     */
    getCurrentSession: () => {
        const sessionId = localStorage.getItem(STORAGE_KEYS.CURRENT_SESSION);
        let chatType = localStorage.getItem(STORAGE_KEYS.CHAT_TYPE);
        
        // Infer chat type from session ID if not explicitly stored
        if (!chatType && sessionId) {
            chatType = SessionManager.getChatType(sessionId);
        }
        
        return {
            chatId: localStorage.getItem(STORAGE_KEYS.CURRENT_CHAT_ID),
            sessionId: sessionId,
            chatType: chatType
        };
    },
    
    /**
     * Determines the type of chat based on the session ID
     * @param {string} sessionId - The session ID to check
     * @returns {string} 'standard' or 'advanced'
     */
    getChatType: (sessionId) => {
        if (!sessionId) return null;
        
        if (sessionId.startsWith(SESSION_PREFIXES.ADVANCED)) {
            return 'advanced';
        } else {
            return 'standard';
        }
    },
    
    /**
     * Generates a new session ID for the given chat type
     * @param {string} type - The chat type ('standard' or 'advanced')
     * @returns {string} A new session ID
     */
    generateSessionId: (type) => {
        const timestamp = Date.now();
        if (type === 'advanced') {
            return `${SESSION_PREFIXES.ADVANCED}${timestamp}`;
        } else {
            return `${SESSION_PREFIXES.STANDARD}${timestamp}`;
        }
    },
    
    /**
     * Creates cookie with expiration date as alternative to localStorage
     * @param {string} name - Cookie name
     * @param {string} value - Cookie value
     * @param {number} days - Days until expiration
     */
    setCookie: (name, value, days = 7) => {
        const expires = new Date();
        expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
        document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
    },
    
    /**
     * Gets cookie value by name
     * @param {string} name - Cookie name
     * @returns {string|null} Cookie value or null if not found
     */
    getCookie: (name) => {
        const nameEQ = `${name}=`;
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
        }
        return null;
    },
    
    /**
     * Removes a cookie by name
     * @param {string} name - Cookie name
     */
    removeCookie: (name) => {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    },
    
    /**
     * Saves session info to both localStorage and cookies for redundancy
     * @param {Object} params - Session parameters
     * @param {string|number} [params.chatId] - The chat ID
     * @param {string} [params.sessionId] - The session ID
     * @param {string} [params.chatType] - The chat type ('standard' or 'advanced')
     */
    saveSessionRedundantly: (params) => {
        const { chatId, sessionId, chatType } = params;
        
        // Save to localStorage
        SessionManager.saveCurrentSession({ chatId, sessionId, chatType });
        
        // Save to cookies as backup
        if (chatId) {
            SessionManager.setCookie(STORAGE_KEYS.CURRENT_CHAT_ID, chatId.toString());
        }
        
        if (sessionId) {
            SessionManager.setCookie(STORAGE_KEYS.CURRENT_SESSION, sessionId);
        }
        
        if (chatType) {
            SessionManager.setCookie(STORAGE_KEYS.CHAT_TYPE, chatType);
        }
    },
    
    /**
     * Gets session info from localStorage or falls back to cookies
     * @returns {Object} Session info with chatId, sessionId, and chatType
     */
    getSessionRedundantly: () => {
        // Try localStorage first
        const localStorageSession = SessionManager.getCurrentSession();
        
        // If any value is missing, try to restore from cookies
        if (!localStorageSession.chatId) {
            localStorageSession.chatId = SessionManager.getCookie(STORAGE_KEYS.CURRENT_CHAT_ID);
        }
        
        if (!localStorageSession.sessionId) {
            localStorageSession.sessionId = SessionManager.getCookie(STORAGE_KEYS.CURRENT_SESSION);
        }
        
        if (!localStorageSession.chatType) {
            localStorageSession.chatType = SessionManager.getCookie(STORAGE_KEYS.CHAT_TYPE);
            
            // If still no chat type, infer from session ID
            if (!localStorageSession.chatType && localStorageSession.sessionId) {
                localStorageSession.chatType = SessionManager.getChatType(localStorageSession.sessionId);
            }
        }
        
        return localStorageSession;
    },
    
    /**
     * Clears session info from both localStorage and cookies
     */
    clearSessionRedundantly: () => {
        SessionManager.clearCurrentSession();
        SessionManager.removeCookie(STORAGE_KEYS.CURRENT_CHAT_ID);
        SessionManager.removeCookie(STORAGE_KEYS.CURRENT_SESSION);
        SessionManager.removeCookie(STORAGE_KEYS.CHAT_TYPE);
    }
};

export default SessionManager;